/**
 * Every route this app serves, and the head copy for each one.
 *
 * ONE TABLE, TWO LISTS
 *   Each entry is `public` or `private`. A public route is offered to
 *   search engines. A private route emits `noindex, nofollow` and nothing
 *   else — no description, no canonical, no share card.
 *   Every route in `src/App.js` must appear here exactly once.
 *   See docs/aeo/AEO-LEDGER.md, task 6.
 *
 * THE ORDER MATTERS
 *   This list mirrors the <Switch> in `src/App.js`, top to bottom, and the
 *   first match wins — the same rule react-router uses. Matching is done by
 *   react-router's own `matchPath`, so the two can never disagree about
 *   what a path means.
 *
 * ALL COPY COMES FROM yedding-brain/BRAND-CONTEXT.md
 *   Never invent a fact about Yedding here. Read §7 (claims policy) and
 *   §12 (words to avoid) before you change one word of it.
 *
 * WHO EDITS THIS FILE NEXT
 *   Task 9      added the four legal routes.
 *   Task 10 added /faq. Task 11a added /about. Task 11b added
 *              /how-it-works. Task 12 adds the guides.
 *   Task 14     adds /venue/:slug and sets the canonical for a venue.
 *   Task 21     replaces the catch-all fallback with a real 404.
 */

export const PUBLIC = 'public';
export const PRIVATE = 'private';

/** Values shared by every page. Sourced from yedding-brain/BRAND-CONTEXT.md §8. */
export const SITE = {
  origin: 'https://yedding.com',
  name: 'Yedding',
  locale: 'en_US',
  twitterCard: 'summary_large_image',
  image: 'https://yedding.com/og-image.jpg',
  imageWidth: '1075',
  imageHeight: '564',
  imageAlt:
    'An outdoor wedding reception at a Yedding venue, with white tables set beneath a large floral centerpiece.',
  // Kept identical to public/index.html, which is the no-JavaScript fallback.
  robotsPublic: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
  robotsPrivate: 'noindex, nofollow'
};

const HOME_TITLE = 'Yedding: Unique Wedding & Event Venues You Can Rent by the Day';
const HOME_DESCRIPTION =
  'Non-traditional event venues with no vendor lock-in. Bring any caterer, photographer or florist you want.';

/** Used only if no entry matches, which the catch-all below prevents. */
export const FALLBACK = {
  path: null,
  visibility: PRIVATE,
  title: HOME_TITLE
};

export const ROUTES = [
  {
    path: '/profile',
    visibility: PRIVATE,
    title: 'Your Profile | Yedding'
  },
  {
    // Public. `canonicalPath` keeps the trailing slash, because that is the
    // URL the server serves and the URL public/sitemap.xml lists.
    // `keepParams` is the whole allow-list: every other query parameter is
    // dropped, so one result set cannot become thousands of duplicate URLs.
    path: '/search',
    visibility: PUBLIC,
    canonicalPath: '/search/',
    keepParams: ['loc'],
    title: 'Search Unique Wedding & Event Venues | Yedding',
    description:
      'Browse non-traditional event venues you can rent by the day. Every venue lets you bring any caterer, photographer or florist you want.'
  },
  {
    path: '/favorites',
    visibility: PRIVATE,
    title: 'Your Favorite Venues | Yedding'
  },
  {
    path: '/reservations',
    visibility: PRIVATE,
    title: 'Your Reservations | Yedding'
  },
  {
    path: '/admin/review/:id',
    visibility: PRIVATE,
    title: 'Review a Venue | Yedding'
  },
  {
    path: '/host/venues',
    visibility: PRIVATE,
    title: 'Your Listed Venues | Yedding'
  },
  {
    path: '/host/reservations',
    visibility: PRIVATE,
    title: 'Bookings at Your Venues | Yedding'
  },
  {
    path: '/host/stripe',
    visibility: PRIVATE,
    title: 'Host Payouts | Yedding'
  },
  {
    path: '/confirmation',
    visibility: PRIVATE,
    title: 'Booking Confirmation | Yedding'
  },
  {
    // Venue-search VS1.2. Hidden: it only makes sense after the requirements.
    path: '/list-your-venue/not-a-fit',
    visibility: PRIVATE,
    title: 'We Cannot List This Venue | Yedding'
  },
  {
    // Venue-search VS1.2: the one link Braeden sends a host. Public (Braeden,
    // 2026-11-02), so owners who search for ways to host events can find it.
    path: '/list-your-venue',
    visibility: PUBLIC,
    canonicalPath: '/list-your-venue',
    keepParams: [],
    title: 'List Your Event Venue | Yedding',
    description:
      'List your property on Yedding and keep 80% of the listed price for all events. Confirm 5 requirements, then create your free account and your listing.'
  },
  {
    // AEO-10: the FAQ. The answers themselves live in src/content/faq.js,
    // and the page emits FAQPage JSON-LD built from that same file.
    path: '/faq',
    visibility: PUBLIC,
    canonicalPath: '/faq',
    keepParams: [],
    title: 'Frequently Asked Questions | Yedding',
    description: 'Answers about booking a Yedding venue: bringing your own vendors, what fees you pay, whole-day bookings, overnight stays, insurance, and listing your own property.'
  },
  {
    // AEO-11a: who runs Yedding. Public on purpose: an answer engine weighs
    // whether a source is run by identifiable people, and this is the page
    // that answers it. The words live in src/content/about.js, and the page
    // emits AboutPage plus one Person node per founder from that same file.
    path: '/about',
    visibility: PUBLIC,
    canonicalPath: '/about',
    keepParams: [],
    title: 'About Yedding | The People Behind the Platform',
    description: 'Yedding is a non-traditional event venue marketplace, founded April 2021 in Salt Lake City, Utah. Owned and run by Brenna Wilkinson Johnson and Braeden Johnson.'
  },
  {
    // AEO-11b: the booking steps. The words live in
    // src/content/how-it-works.js, and the page emits HowTo JSON-LD built
    // from that same file.
    path: '/how-it-works',
    visibility: PUBLIC,
    canonicalPath: '/how-it-works',
    keepParams: [],
    title: 'How It Works | Booking a Yedding Event Venue',
    description: 'How to book a Yedding event venue: find a venue, choose from the listing, pay at checkout, and arrange event insurance if your event needs it.'
  },
  {
    // AEO-9: the four legal documents. Public on purpose — terms and a
    // privacy policy are trust content, and an answer engine looks for
    // them. Their text lives in src/content/legal, one file each.
    path: '/terms',
    visibility: PUBLIC,
    canonicalPath: '/terms',
    keepParams: [],
    title: 'Terms of Service | Yedding',
    description: 'The Terms of Service for the Yedding Platform: the binding agreement between Yedding, LLC and everyone who books or lists an event space.'
  },
  {
    path: '/privacy',
    visibility: PUBLIC,
    canonicalPath: '/privacy',
    keepParams: [],
    title: 'Privacy Policy | Yedding',
    description: 'The Yedding Privacy Policy: the personal information Yedding, LLC collects, how that information is used, and who it is shared with.'
  },
  {
    path: '/host-agreement',
    visibility: PUBLIC,
    canonicalPath: '/host-agreement',
    keepParams: [],
    title: 'Host Terms | Yedding',
    description: 'The Host Terms for Yedding: hosting on Yedding, managing your listing, responsibility for damages, cancellations and taxes.'
  },
  {
    path: '/guest-agreement',
    visibility: PUBLIC,
    canonicalPath: '/guest-agreement',
    keepParams: [],
    title: 'Guest Terms | Yedding',
    description: 'The Guest Terms for Yedding: booking an event space, cancellations and refunds, your responsibilities, and responsibility for damages.'
  },
  {
    path: '/admin/home',
    exact: true,
    visibility: PRIVATE,
    title: 'Admin | Yedding'
  },
  {
    // No route in App.js serves bare `/admin`, so it falls through to Home.
    // This entry stops that page being offered to an engine anyway. It also
    // matches `Disallow: /admin` in public/robots.txt.
    //
    // The three private host routes are listed in full above, exactly as
    // public/robots.txt lists them, rather than as one short `/host` entry.
    // Note the reason is NOT the robots.txt trap: `matchPath` matches whole
    // segments, so `/host` would not swallow task 9's public
    // `/host-agreement` page the way a `Disallow: /host` line would. The
    // full paths are kept because one list a reader can compare against
    // robots.txt, line for line, is worth more than a shorter one.
    path: '/admin',
    visibility: PRIVATE,
    title: 'Admin | Yedding'
  },
  {
    path: '/logout',
    exact: true,
    visibility: PRIVATE,
    title: 'Signing Out | Yedding'
  },
  {
    // Catch-all, and last, exactly as in App.js. Any unknown URL renders
    // Home, so it canonicalises to Home. Task 21 replaces this with a real
    // 404 page.
    path: '/',
    visibility: PUBLIC,
    canonicalPath: '/',
    keepParams: [],
    title: HOME_TITLE,
    description: HOME_DESCRIPTION
  }
];
